import type { Store } from '../store.js';
import {
  activeName,
  addDraftDie,
  addPlayer,
  backToSetup,
  bankPoints,
  bust,
  clearDraft,
  commitSelection,
  diceLeftThisTurn,
  draftScore,
  movePlayerLocal,
  rematch,
  removeDraftDie,
  removePlayer,
  setPlayerName,
  setTargetScore,
  startPlaying,
  undoLastMove,
} from '../local-game.js';
import {
  MAX_TARGET_SCORE,
  MIN_TARGET_SCORE,
  SINGLE_FIVE,
  SINGLE_ONE,
  STRAIGHT_1_5,
  STRAIGHT_2_6,
  STRAIGHT_FULL,
} from '../../shared/rules.js';
import type { DieFace } from '../../shared/types.js';
import { renderDie } from './dice.js';
import { animateCount, celebrate } from './anim.js';
import { makeMuteButton } from './controls.js';
import { playBank, playBust, playHotDice, playSelect, playWin } from '../sound.js';

export function renderLocal(host: HTMLElement, store: Store): void {
  const game = store.get().local;
  if (!game) return;
  host.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'local-mode';

  const topbar = document.createElement('div');
  topbar.className = 'local-topbar';
  const title = document.createElement('h1');
  title.textContent = 'Tisch-Spiel';
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'ghost';
  back.textContent = 'Online-Lobby';
  back.addEventListener('click', () => store.update(s => { s.local = null; }));
  const controls = document.createElement('div');
  controls.className = 'controls';
  controls.append(makeMuteButton(), back);
  topbar.append(title, controls);
  wrap.appendChild(topbar);

  if (game.phase === 'setup') wrap.appendChild(renderSetup(store));
  else if (game.phase === 'playing') wrap.appendChild(renderPlaying(store));
  else wrap.appendChild(renderFinished(store));

  wrap.appendChild(renderRulesCheatSheet());
  if (game.log.length > 0) wrap.appendChild(renderLog(game.log));

  host.appendChild(wrap);
}

function renderSetup(store: Store): HTMLElement {
  lastLocalScores = [];
  lastLocalTurnPts = 0;
  const game = store.get().local!;
  const card = document.createElement('section');
  card.className = 'lobby-card';
  const h = document.createElement('h2');
  h.textContent = 'Setup';
  card.appendChild(h);

  const playersWrap = document.createElement('div');
  playersWrap.className = 'local-players-edit';
  game.players.forEach((p, idx) => {
    const row = document.createElement('div');
    row.className = 'local-player-row';

    const ord = document.createElement('span');
    ord.className = 'order-num';
    ord.textContent = `${idx + 1}.`;

    const up = document.createElement('button');
    up.type = 'button';
    up.className = 'reorder-btn';
    up.textContent = '▲';
    up.title = 'Hoch';
    up.disabled = idx === 0;
    up.addEventListener('click', () => store.update(s => { if (s.local) movePlayerLocal(s.local, idx, 'up'); }));

    const down = document.createElement('button');
    down.type = 'button';
    down.className = 'reorder-btn';
    down.textContent = '▼';
    down.title = 'Runter';
    down.disabled = idx === game.players.length - 1;
    down.addEventListener('click', () => store.update(s => { if (s.local) movePlayerLocal(s.local, idx, 'down'); }));

    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 20;
    input.placeholder = `Spieler ${idx + 1}`;
    input.value = p.name;
    input.addEventListener('change', () => {
      store.update(s => { if (s.local) setPlayerName(s.local, idx, input.value); });
    });

    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'ghost icon';
    rm.textContent = '−';
    rm.title = 'Entfernen';
    rm.disabled = game.players.length <= 2;
    rm.addEventListener('click', () => store.update(s => { if (s.local) removePlayer(s.local, idx); }));

    row.append(ord, up, down, input, rm);
    playersWrap.appendChild(row);
  });
  card.appendChild(playersWrap);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'ghost';
  addBtn.textContent = '+ Spieler';
  addBtn.disabled = game.players.length >= 8;
  addBtn.addEventListener('click', () => store.update(s => { if (s.local) addPlayer(s.local); }));
  card.appendChild(addBtn);

  const targetLabel = document.createElement('label');
  targetLabel.textContent = 'Zielpunktzahl';
  const targetInput = document.createElement('input');
  targetInput.type = 'number';
  targetInput.min = String(MIN_TARGET_SCORE);
  targetInput.max = String(MAX_TARGET_SCORE);
  targetInput.step = '500';
  targetInput.value = String(game.targetScore);
  targetInput.addEventListener('change', () => {
    store.update(s => { if (s.local) setTargetScore(s.local, Number(targetInput.value)); });
  });
  targetLabel.appendChild(targetInput);
  card.appendChild(targetLabel);

  const startBtn = document.createElement('button');
  startBtn.type = 'button';
  startBtn.className = 'primary big';
  startBtn.textContent = 'Spiel starten';
  startBtn.disabled = game.players.filter(p => p.name.trim().length > 0).length < 2;
  startBtn.addEventListener('click', () => {
    store.update(s => { if (s.local) startPlaying(s.local); });
  });
  card.appendChild(startBtn);

  const note = document.createElement('p');
  note.className = 'phase-msg muted';
  note.textContent = 'Mindestens 2 benannte Spieler. Du würfelst echte Würfel, die App zählt nur.';
  card.appendChild(note);

  return card;
}

function renderPlaying(store: Store): HTMLElement {
  const game = store.get().local!;
  const wrap = document.createElement('div');
  wrap.className = 'phase-area playing local-playing';

  const head = document.createElement('div');
  head.className = 'turn-info';
  head.innerHTML = `
    <div class="turn-line">
      <span class="who">${escapeHtml(activeName(game))} ist am Zug</span>
      <span class="turn-points">Zug-Punkte: <strong>${game.turnPoints}</strong></span>
    </div>
    <div class="turn-sub">${diceLeftThisTurn(game)} Würfel zu werfen ${game.hotDiceFlag ? ' · Hot Dice!' : ''}</div>
  `;
  wrap.appendChild(head);

  const tpEl = head.querySelector('.turn-points strong') as HTMLElement | null;
  if (tpEl) {
    if (game.turnPoints > lastLocalTurnPts) {
      tpEl.textContent = String(lastLocalTurnPts);
      animateCount(tpEl, lastLocalTurnPts, game.turnPoints, 450);
      tpEl.classList.add('bumped');
    }
    lastLocalTurnPts = game.turnPoints;
  }

  const scoreboard = document.createElement('div');
  scoreboard.className = 'players';
  const tracked = lastLocalScores.length === game.players.length;
  game.players.forEach((p, idx) => {
    const card = document.createElement('div');
    card.className = 'player';
    if (idx === game.activeIdx) card.classList.add('active');
    const nameDiv = document.createElement('div');
    nameDiv.className = 'player-name';
    nameDiv.textContent = p.name;
    const scoreDiv = document.createElement('div');
    scoreDiv.className = 'player-score';
    const prev = tracked ? lastLocalScores[idx] : p.score;
    if (prev !== p.score) {
      scoreDiv.textContent = String(prev);
      animateCount(scoreDiv, prev, p.score);
      if (p.score > prev) scoreDiv.classList.add('score-bump');
    } else {
      scoreDiv.textContent = String(p.score);
    }
    card.append(nameDiv, scoreDiv);
    scoreboard.appendChild(card);
  });
  lastLocalScores = game.players.map(p => p.score);
  wrap.appendChild(scoreboard);

  const tray = document.createElement('div');
  tray.className = 'dice-tray';
  game.draftDice.forEach((face, i) => {
    tray.appendChild(renderDie({ value: face, held: true, locked: false }, {
      index: i,
      selectable: true,
      onClick: (idx) => store.update(s => { if (s.local) removeDraftDie(s.local, idx); }),
    }));
  });
  if (game.draftDice.length === 0) {
    const placeholder = document.createElement('div');
    placeholder.className = 'tray-placeholder';
    placeholder.textContent = 'Tippe unten auf die Augenzahl, die du beiseitelegst.';
    tray.appendChild(placeholder);
  }
  wrap.appendChild(tray);

  const adder = document.createElement('div');
  adder.className = 'face-adder';
  const left = diceLeftThisTurn(game);
  for (let face = 1 as DieFace; face <= 6; face = (face + 1) as DieFace) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'face-btn';
    btn.dataset.face = String(face);
    btn.textContent = String(face);
    btn.disabled = left <= 0;
    btn.addEventListener('click', () => {
      let added = false;
      store.update(s => { if (s.local) added = addDraftDie(s.local, face); });
      if (added) playSelect();
    });
    adder.appendChild(btn);
    if (face === 6) break;
  }
  wrap.appendChild(adder);

  const sel = draftScore(game);
  const preview = document.createElement('div');
  preview.className = 'selection-preview';
  if (game.draftDice.length === 0) {
    preview.textContent = 'Auswahl leer.';
  } else if (!sel.valid) {
    preview.classList.add('invalid');
    preview.textContent = 'Auswahl ergibt keine gültige Wertung.';
  } else {
    preview.textContent = `Auswahl: +${sel.points}`;
  }
  wrap.appendChild(preview);

  const actions = document.createElement('div');
  actions.className = 'actions';
  const commit = document.createElement('button');
  commit.type = 'button';
  commit.className = 'primary';
  commit.textContent = 'Werten';
  commit.disabled = !sel.valid || game.draftDice.length === 0;
  commit.addEventListener('click', () => {
    let ok = false, hot = false;
    store.update(s => {
      if (s.local) { ok = commitSelection(s.local).ok; if (ok) hot = s.local.hotDiceFlag; }
    });
    if (ok) hot ? playHotDice() : playSelect();
  });

  const bankBtn = document.createElement('button');
  bankBtn.type = 'button';
  bankBtn.className = 'secondary';
  const bankTotal = game.turnPoints + (sel.valid ? sel.points : 0);
  bankBtn.textContent = `Banken (${bankTotal})`;
  bankBtn.disabled = bankTotal <= 0 || (game.draftDice.length > 0 && !sel.valid);
  bankBtn.addEventListener('click', () => {
    let ok = false, finished = false;
    store.update(s => {
      if (s.local) { ok = bankPoints(s.local).ok; if (ok) finished = s.local.phase === 'finished'; }
    });
    if (ok) { playBank(); if (finished) { playWin(); celebrate(); } }
  });

  const bustBtn = document.createElement('button');
  bustBtn.type = 'button';
  bustBtn.className = 'danger';
  bustBtn.textContent = 'Bust';
  bustBtn.title = 'Wurf ohne Wertung — Zugpunkte verloren';
  bustBtn.addEventListener('click', () => {
    store.update(s => { if (s.local) bust(s.local); });
    playBust();
  });

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'ghost';
  clearBtn.textContent = 'Auswahl leeren';
  clearBtn.disabled = game.draftDice.length === 0;
  clearBtn.addEventListener('click', () => store.update(s => { if (s.local) clearDraft(s.local); }));

  const undoBtn = document.createElement('button');
  undoBtn.type = 'button';
  undoBtn.className = 'ghost';
  undoBtn.textContent = game.prevSnapshot ? `Rückgängig (${game.prevSnapshot.label})` : 'Rückgängig';
  undoBtn.title = 'Letzte Wertung/Banken/Bust zurücknehmen — nur einmal möglich.';
  undoBtn.disabled = !game.prevSnapshot;
  undoBtn.addEventListener('click', () => store.update(s => { if (s.local) undoLastMove(s.local); }));

  actions.append(commit, bankBtn, bustBtn, clearBtn, undoBtn);
  wrap.appendChild(actions);

  return wrap;
}

function renderFinished(store: Store): HTMLElement {
  lastLocalScores = [];
  lastLocalTurnPts = 0;
  const game = store.get().local!;
  const wrap = document.createElement('div');
  wrap.className = 'phase-area finished';
  const winner = game.winnerIdx != null ? game.players[game.winnerIdx] : null;
  const h = document.createElement('h2');
  h.textContent = winner ? `${winner.name} gewinnt!` : 'Spiel beendet.';
  wrap.appendChild(h);

  const ol = document.createElement('ol');
  ol.className = 'final-scores';
  for (const p of [...game.players].sort((a, b) => b.score - a.score)) {
    const li = document.createElement('li');
    li.innerHTML = `<span>${escapeHtml(p.name)}</span><strong>${p.score}</strong>`;
    ol.appendChild(li);
  }
  wrap.appendChild(ol);

  const again = document.createElement('button');
  again.type = 'button';
  again.className = 'primary big';
  again.textContent = 'Nochmal';
  again.addEventListener('click', () => store.update(s => { if (s.local) rematch(s.local); }));

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'ghost';
  reset.textContent = 'Spieler ändern';
  reset.title = 'Zurück ins Setup, Namen bleiben.';
  reset.addEventListener('click', () => store.update(s => { if (s.local) backToSetup(s.local); }));

  const actions = document.createElement('div');
  actions.className = 'actions';
  actions.append(again, reset);
  if (game.prevSnapshot) {
    const undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.className = 'ghost';
    undoBtn.textContent = `Rückgängig (${game.prevSnapshot.label})`;
    undoBtn.title = 'Letzten Zug zurücknehmen — nur einmal möglich.';
    undoBtn.addEventListener('click', () => store.update(s => { if (s.local) undoLastMove(s.local); }));
    actions.append(undoBtn);
  }
  wrap.appendChild(actions);

  return wrap;
}

function renderRulesCheatSheet(): HTMLElement {
  const det = document.createElement('details');
  det.className = 'rules';
  det.open = rulesOpen;
  det.addEventListener('toggle', () => { rulesOpen = det.open; });
  const sum = document.createElement('summary');
  sum.textContent = 'Wertungstabelle';
  det.appendChild(sum);
  const rows: [string, string][] = [
    ['Einzelne 1', String(SINGLE_ONE)],
    ['Einzelne 5', String(SINGLE_FIVE)],
    ['Drei 1er', '1000'],
    ['Drei 2er / 3er / 4er / 5er / 6er', '200 / 300 / 400 / 500 / 600'],
    ['Vier gleiche', '2× Drilling'],
    ['Fünf gleiche', '4× Drilling'],
    ['Sechs gleiche', '8× Drilling'],
    ['Straße 1–5', String(STRAIGHT_1_5)],
    ['Straße 2–6', String(STRAIGHT_2_6)],
    ['Volle Straße 1–6', String(STRAIGHT_FULL)],
  ];
  const table = document.createElement('table');
  for (const [k, v] of rows) {
    const tr = document.createElement('tr');
    tr.innerHTML = `<td>${k}</td><td>${v}</td>`;
    table.appendChild(tr);
  }
  det.appendChild(table);
  return det;
}

function renderLog(entries: string[]): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'log';
  for (const msg of entries.slice(-10).reverse()) {
    const div = document.createElement('div');
    div.className = 'log-entry';
    div.textContent = msg;
    wrap.appendChild(div);
  }
  wrap.addEventListener('scroll', () => { logScrollTop = wrap.scrollTop; });
  queueMicrotask(() => { wrap.scrollTop = logScrollTop; });
  return wrap;
}

let rulesOpen = false;
let logScrollTop = 0;
// Count-up-Tracker — bei Nicht-Spielphasen geleert, damit ein Neustart nicht rückwärts zählt.
let lastLocalScores: number[] = [];
let lastLocalTurnPts = 0;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
